import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { SafeReportResponse } from "../Conversion";
import { useCallback, useState } from "react";
import { downloadUawSheet } from "../../../api/routes";
import { downloadBlob } from "../../../util";
import { EnumEditor } from "../sov/CellEditors";
import styles from "./UawCard.module.css";
import { useUpdateReport } from "../data";
import * as client from "../../../client";
import { InputText } from "primereact/inputtext";
import { debounce, groupBy } from "lodash";
import React from "react";
import { Accordion, AccordionTab } from "primereact/accordion";

type UawQuestion = {
  spreadsheetRow: string;
  question: string;
  enumOptions?: string[];
};

const yesNoOptions = ["Yes", "No"];

const SECTION_NAMES: { [key: string]: string } = {
  rating: "Rating",
  risk_meter: "Risk Meter",
};

const UAW_QUESTIONS: { [key: string]: UawQuestion } = {
  "24": {
    spreadsheetRow: "24",
    question: "Risk Meter Score > 80?",
    enumOptions: yesNoOptions,
  },
  "25": {
    spreadsheetRow: "25",
    question:
      "Reviewed Risk Meter and applied appropriate deductibles/roof endts",
    enumOptions: ["Done"],
  },
  "92": {
    spreadsheetRow: "92",
    question: "Manual premium > $100,000 (prior to IRPM)",
    enumOptions: yesNoOptions,
  },
  "93": {
    spreadsheetRow: "93",
    question: "Monoline GL accounts with a premium > $50K?",
    enumOptions: yesNoOptions,
  },
  "94": {
    spreadsheetRow: "94",
    question: "Accounts with >$15M of TIV at one loc?",
    enumOptions: yesNoOptions,
  },
  "95": {
    spreadsheetRow: "95",
    question: "Accounts with > $75M TIV at all locations?",
    enumOptions: yesNoOptions,
  },
  "96": {
    spreadsheetRow: "96",
    question: "Is Automatic Increase <4%?",
    enumOptions: yesNoOptions,
  },
  "97": {
    spreadsheetRow: "97",
    question: "Reference Rate history for past 2 Years:",
    enumOptions: ["Done", "N/A"],
  },
  "98": {
    spreadsheetRow: "98",
    question:
      "RATE INCREASE & Justification in NOTES Section:\nRenewals: Follow Target Grid(LINK)",
  },
  "99": {
    spreadsheetRow: "99",
    question: "Multi-Location Factor?",
    enumOptions: yesNoOptions,
  },
  "100": {
    spreadsheetRow: "100",
    question:
      "Eligibility evaluation including unique exposures, risk experience, and evaluation - Include Notes in Comments and elaborate if there is any reason for deviating from rules:",
    enumOptions: ["Highly Preferred", "Preferred", "Standard", "N/A"],
  },
  "101": {
    spreadsheetRow: "101",
    question: "Broker Fee:",
    enumOptions: yesNoOptions,
  },
  "102": {
    spreadsheetRow: "102",
    question:
      "Policy forms other than those listed in the Program Administrator’s Manual?",
    enumOptions: yesNoOptions,
  },
  "103": {
    spreadsheetRow: "103",
    question:
      "Located in one of the below states? If yes, calculate taxes/fees in Inscipher and Add to Additional Tax box in CC:\nKY - Municipality Fee: up to 10%\nSD 0.175% (no Fire Marshall for Excess), IL 1% , OR 0.3% and $10 Surplus Charge, MT 2.5% - Fire Marshall\nCO, OK, SD, TN, WY - SLAS Fee/Clearing House Fee: 0.175% \nVA - Annual Assessment Fee: 0.025% \nFL - EMPA Fee: $4",
    enumOptions: yesNoOptions,
  },
  "104": {
    spreadsheetRow: "104",
    question: "Rate change calculator reviewed?",
    enumOptions: yesNoOptions,
  },
  "105": {
    spreadsheetRow: "105",
    question: "Rate need for accounts with losses",
    enumOptions: yesNoOptions,
  },
};

type UawCardProps = {
  specialtyPropertyInfo: SafeReportResponse;
};

export const UawCard: React.FC<UawCardProps> = ({ specialtyPropertyInfo }) => {
  const [isLoading, setIsLoading] = useState(false);
  const updateReport = useUpdateReport(specialtyPropertyInfo.report_id);

  const questionsOnReport = specialtyPropertyInfo.report_json.uaw_info
    ? specialtyPropertyInfo.report_json.uaw_info.questions
    : {};
  const precomputedQuestions = specialtyPropertyInfo.precomputed_metadata
    .uaw_info
    ? specialtyPropertyInfo.precomputed_metadata.uaw_info.questions
    : {};

  const originalQuestions = Object.entries(questionsOnReport).map(
    ([key, question]) => {
      const precomputedAnswer = precomputedQuestions[key]?.answer;
      const filledOutQuestion =
        !question.answer && precomputedAnswer
          ? { ...question, answer: precomputedAnswer }
          : question;

      return filledOutQuestion;
    }
  );

  const questionsBySection = groupBy(originalQuestions, "section");

  return (
    <Card
      header={
        <div
          className={styles.horizontalContainer}
          style={{
            paddingTop: "20px",
            paddingLeft: "20px",
          }}
        >
          <span className="p-card-title" style={{ marginRight: "12px" }}>
            UAW
          </span>
          <Button
            raised
            text
            loading={isLoading}
            disabled={updateReport.isPending}
            label="Generate UAW Sheet"
            onClick={async () => {
              setIsLoading(true);
              const blob = await downloadUawSheet(
                specialtyPropertyInfo.report_id
              );
              const filename = `UAW 2025-2026.xlsm`;
              downloadBlob(filename, blob);
              setIsLoading(false);
            }}
          />
        </div>
      }
    >
      <Accordion activeIndex={0}>
        {Object.entries(questionsBySection).map(([section, questions]) => (
          <AccordionTab header={SECTION_NAMES[section]}>
            <div className={styles.questionContainer}>
              {questions.map((question) => {
                const [textValue, setTextValue] = useState(question.answer);
                const onEdit = (newValue: string) => {
                  const spreadsheetRow = question.spreadsheet_row;
                  const newUawInfo: client.UawInfo = {
                    questions: {
                      ...questionsOnReport,
                      [spreadsheetRow]: {
                        spreadsheet_row: question.spreadsheet_row,
                        answer: newValue,
                        section: question.section,
                      },
                    },
                  };
                  updateReport.mutate({ uawInfo: newUawInfo });
                };

                const debouncedOnEdit = useCallback(
                  debounce((value) => onEdit(value), 1000),
                  [questionsOnReport]
                );

                if (!(question.spreadsheet_row in UAW_QUESTIONS)) {
                  return <></>;
                }

                const localQuestion = UAW_QUESTIONS[question.spreadsheet_row];
                return (
                  <React.Fragment key={question.spreadsheet_row}>
                    <div className={styles.question}>
                      <span
                        style={{
                          whiteSpace: "pre-wrap",
                          fontWeight: "bold",
                          fontSize: "14px",
                        }}
                      >
                        {localQuestion.question}
                      </span>
                      <div>
                        {localQuestion.enumOptions ? (
                          <EnumEditor
                            options={{
                              value: question.answer,
                              editorCallback: (value) => onEdit(value),
                            }}
                            enumValues={localQuestion.enumOptions}
                            disabled={isLoading || updateReport.isPending}
                            additionalStyles={{
                              height: "26px",
                              width: "120px",
                            }}
                            pt={{ input: { style: { paddingTop: "4px" } } }}
                          />
                        ) : (
                          <InputText
                            style={{ height: "26px", width: "120px" }}
                            value={textValue ?? undefined}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              setTextValue(newValue);
                              debouncedOnEdit(newValue);
                            }}
                            disabled={isLoading || updateReport.isPending}
                          />
                        )}
                      </div>
                    </div>
                    <div
                      className={styles.line}
                      key={"line_" + question.spreadsheet_row}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </AccordionTab>
        ))}
      </Accordion>
    </Card>
  );
};
